/**
 * Geography questions: the one rule that names a study's market questions (M4S-5608).
 *
 * Every TPBP starts with "STATE / DMA - Market - Total", and Juhi's Table 1 is the same table, so a
 * new crosstab lists each market question in Rows at its survey position. This module owns the two
 * things everyone else needs to know about them:
 *
 * - **Which questions they are** (`isGeographyQuestion`). Exact base QIDs, in one constant, so
 *   widening the list is a visible, reviewed change. The base column is read the way
 *   `computeFromApi` reads it (`responseColumnId ?? qid`), so a later wave's `DMA__v2` counts.
 *   `scale.ts` calls this too: Nielsen market codes (501, 506, ...) look like a scale to anything
 *   that reads codes, and a market table must never show Top 2 Box, a Mean or a Median.
 * - **How they join a new crosstab's rows** (`withGeographyRows`), which the starting setup calls
 *   once, on a create. It never runs against an existing record, so removing the table with the
 *   pill's X or Clear All sticks; there is no "removed" flag because nothing ever adds it back.
 *
 * The module is pure and imports only `survey-order.ts`, so Package F's rule (a list still in
 * survey order gets an id at its position; a list someone rearranged gets it appended) is the same
 * one every other entry point uses.
 */

import {
    insertAllAtSurveyPosition,
    sortQuestionsBySurveyOrder,
    type OrderableQuestion,
    type OrderableVariable,
} from '@/lib/crosstab/survey-order';

/** The base QIDs of a study's market questions. */
export const GEOGRAPHY_QIDS = ['DMA', 'STATE'] as const;

/** A catalog question as this module reads it. Both the API DTO and the rail catalog satisfy it. */
export type GeographyQuestion = OrderableQuestion & { qid: string; responseColumnId?: string };

/**
 * True when the question's base column is a market question, in any case.
 *
 * `responseColumnId` is the original column name, which a wave variant (`DMA__v2`) keeps while its
 * `qid` gets the suffix. Look-alikes such as DMACV, USSTATES and STATEREGION are not geography.
 */
export function isGeographyQuestion(q: { qid: string; responseColumnId?: string }): boolean {
    const base = (q.responseColumnId ?? q.qid).toUpperCase();
    return (GEOGRAPHY_QIDS as readonly string[]).includes(base);
}

/**
 * The catalog ids of the market questions, wave variants included, in survey order.
 *
 * When only some waves carry the question it sorts after the shared block, as the survey-order
 * rule puts it, rather than to the top.
 */
export function geographyRowIds(catalog: readonly GeographyQuestion[]): string[] {
    return sortQuestionsBySurveyOrder(catalog)
        .filter(isGeographyQuestion)
        .map((q) => q.qid);
}

/**
 * `rows` with every market question inserted at its survey position.
 *
 * - **Skips what the banner already holds.** A default banner with fewer than three of
 *   Gender/Age/Ethnicity/Income falls back to DMA, and DMA must sit in exactly one list. The
 *   comparison is on the base column, so `DMA__v2` is skipped along with `DMA`.
 * - **`variables` and `banner` are required.** Without the variables, a net already in the rows
 *   makes the list look rearranged and geography gets appended after it instead of placed.
 * - **Idempotent, and never mutates its inputs.** An id already in the list is left where it is,
 *   wherever it was dragged to, and a catalog with no market question returns `rows` unchanged.
 */
export function withGeographyRows(
    rows: readonly string[],
    ctx: {
        catalog: readonly GeographyQuestion[];
        variables: readonly OrderableVariable[];
        banner: readonly string[];
    },
): string[] {
    const { catalog, variables, banner } = ctx;

    const byId = new Map(catalog.map((q) => [q.qid.toUpperCase(), q] as const));
    const bannerBases = new Set(
        banner.map((id) => {
            const q = byId.get(id.toUpperCase());
            // A custom variable is not in the catalog: its own id is all there is to compare.
            return (q ? (q.responseColumnId ?? q.qid) : id).toUpperCase();
        }),
    );

    const ids = geographyRowIds(catalog).filter((id) => {
        const q = byId.get(id.toUpperCase());
        return !bannerBases.has((q?.responseColumnId ?? id).toUpperCase());
    });

    return insertAllAtSurveyPosition(rows, ids, catalog, variables);
}
